import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import Card from '@material-ui/core/Card';
import Slider from '@material-ui/core/Slider';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import Typography from '@material-ui/core/Typography';

import { formatAmountValue, fractionToPercent, translatePortfolioCode, translatePortfolioType } from 'helpers/helpers';
import Info from 'components/Tooltip/Info';
import FreeSwitch from 'components/FormFields/FreeSwitch';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';

import {
  Bucket as BucketType,
  Bucket,
  PortfolioType,
} from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';
import useStyles, { useSliderStyles } from './styles';
import { EdwardColors, Currency } from 'enums';
import { WorkflowPlanStore } from 'stores/workflowStores/newWorkflowPlanStore/workflowPlanStore';

interface OwnProps {
  store?: WorkflowPlanStore;
  subtitleMore?: string;
  activated?: boolean;
  accountId: string;
  onRiskChange: Function;
  currency: Currency;
  readOnly?: boolean;
  showAdditionalInfo?: boolean;
  showDetail?: Function;
  mobile?: boolean;
  tablet?: boolean;
  currencyHedgingEnabled: boolean;
  rebalancingEnabled: boolean;
  rebalancingTimeTestDisabled?: boolean;
  userMode?: boolean;
  detailMode?: boolean;
  profileLockActive?: boolean;
  profileLockDuration?: number;
  anyRiskChange?: boolean;
  overview?: boolean;
  originalBucket?: BucketType;
}

type Props = Bucket & OwnProps;

interface Colors {
  [key: string]: string;
}

export const colors: Colors = {
  SHORT_TERM: EdwardColors.LIGHTER,
  MID_TERM: EdwardColors.DARK,
  LONG_TERM: EdwardColors.SECONDARY,

  SHORT_TERM_ALT: EdwardColors.GRAY_LIGHT,
  MID_TERM_ALT: EdwardColors.GRAY_LIGHTER,
  LONG_TERM_ALT: EdwardColors.GRAY_MEDIUM,

  SHORT_TERM_TITLE: EdwardColors.MEDIUM,
  MID_TERM_TITLE: EdwardColors.DARK,
  LONG_TERM_TITLE: EdwardColors.SECONDARY,

  ESG: EdwardColors.GREEN,

  K1: EdwardColors.LIGHTER,
  K2: EdwardColors.DARK,
  K3: EdwardColors.SECONDARY,
};

interface ColorOpacity {
  [key: string]: number;
}

export const colorOpacity: ColorOpacity = {
  SHORT_TERM_OPACITY: 0.85,
  MID_TERM_OPACITY: 0.8,
  LONG_TERM_OPACITY: 0.85,
};

const bucketNumber = {
  SHORT_TERM: 0,
  MID_TERM: 1,
  LONG_TERM: 2,
};

const BucketComponent: React.FC<Props> = (props) => {
  const {
    active,
    title,
    subtitle,
    type,
    balance,
    portfolio,
    originalRiskProfile,
    riskProfile,
    minProfile,
    maxProfile,
    reserved,
    currency,
    readOnly,
    automaticRebalancing,
    rebalancingTimeTest,
    currencyHedging,
    showAdditionalInfo,
    showDetail,
    mobile,
    tablet,
    store,
    //currencyHedgingEnabled,
    rebalancingEnabled,
    rebalancingTimeTestDisabled,
    userMode,
    detailMode,
    profileLockActive,
    profileLockDuration,
    anyRiskChange,
    overview,
    originalBucket,
  } = props;

  const parsedRisk = parseInt((riskProfile as string) || '0');
  const parsedOriginalRiskProfile = parseInt(
    (originalBucket ? (originalBucket.riskProfile as string) : (originalRiskProfile as string)) || '0',
  );
  const parsedMinRisk = parseInt((minProfile as string) || '0');
  const parsedMaxRisk = parseInt((maxProfile as string) || '0');

  const [risk, setRisk] = useState<number | number[]>(parsedRisk);
  const [oneTimeRebalanceOpen, setOneTimeRebalanceOpen] = useState(false);
  const [fixedTaxWarning, setFixedTaxWarning] = useState(false);
  const [timeTestOpen, setTimeTestOpen] = useState(false);

  useEffect(() => {
    setRisk(parsedRisk);
  }, [parsedRisk]);

  const { t } = useTranslation();
  const bgColor = colors[`${type}${!active ? '_ALT' : ''}`];
  const classes = useStyles({ ...props, bgColor, active });
  const sliderClasses = useSliderStyles();
  const thisBucketNumber = bucketNumber[props.type as keyof typeof bucketNumber];

  const name = `buckets[${thisBucketNumber}].riskProfile`;
  const marks = Array.from(Array(parsedMaxRisk - parsedMinRisk + 1), (_, i) => i + parsedMinRisk);

  const riskChange = (originalRiskProfile || originalBucket) && parsedOriginalRiskProfile !== parsedRisk;

  // TODO: remove hedging setting

  return (
    <Card className={`${classes.bucket} ${!active ? classes.inactive : ''}`}>
      <div className={classes.header}>
        <Box position="relative">
          <Typography variant="h2" className={`${props.activated ? '_active' : ''}`}>
            <span>
              {t(title)}
              {!active && ` ${t('general_inactive')}`}
            </span>
          </Typography>
          <div>
            <Typography>
              {subtitle && t(subtitle)}&nbsp;
              {reserved &&
                reserved.value.value > 0 &&
                `${t('bucket_plusReserve')} ${formatAmountValue(reserved.value.value, currency)}`}
            </Typography>
            <Typography variant="h2" align="right">
              {formatAmountValue(balance.value, currency)}
            </Typography>
          </div>
        </Box>
      </div>
      <div className={classes.body}>
        <Box>
          {(riskChange || anyRiskChange) && (
            <>
              <Typography
                className={`${classes.risk} ${anyRiskChange && !riskChange ? classes.dummy : ''}`}
                style={{ color: '#818284' }}
              >
                {t(
                  tablet && !mobile ? 'general_portfolioStrategyShortPrev' : 'general_portfolioStrategyPrev',
                ).toUpperCase()}
                <Info content={t('tooltip_plan_portfolioStrategy_prev')} heading className={classes.tooltip} />
              </Typography>

              <Slider
                name={`${name}_previous`}
                value={parsedOriginalRiskProfile}
                marks={new Array(parsedOriginalRiskProfile).fill(0).map((value, i) => ({
                  value: i + 1,
                  label: i + 1,
                }))}
                aria-labelledby="discrete-slider"
                valueLabelDisplay="auto"
                step={null}
                min={1}
                max={10}
                disabled={true}
                className={anyRiskChange && !riskChange ? classes.dummy : undefined}
              />
            </>
          )}

          <Typography className={`${classes.risk} ${riskChange && classes.riskChange}`}>
            {riskChange
              ? t(
                  tablet && !mobile ? 'general_portfolioStrategyShortCurrent' : 'general_portfolioStrategyCurrent',
                ).toUpperCase()
              : t(tablet && !mobile ? 'general_portfolioStrategyShort' : 'general_portfolioStrategy').toUpperCase()}
            <Info
              content={
                profileLockActive ? (
                  t('tooltip_plan_portfolioStrategy_locked', { days: profileLockDuration })
                ) : riskChange ? (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t('tooltip_plan_portfolioStrategy_change', {
                        interpolation: { escapeValue: false },
                      }),
                    }}
                  />
                ) : (
                  t('tooltip_plan_portfolioStrategy')
                )
              }
              heading
              className={classes.tooltip}
            />
          </Typography>

          <Slider
            name={name}
            value={risk}
            marks={marks
              .filter(function (item, pos) {
                return marks.indexOf(item) === pos;
              })
              .map((value) => ({
                value: value,
                label: value,
              }))}
            aria-labelledby="discrete-slider"
            valueLabelDisplay="auto"
            step={null}
            min={1}
            max={10}
            onChange={(_e, value) => setRisk(value)}
            onChangeCommitted={(e, value) => {
              if (value !== parsedRisk) {
                if (detailMode) {
                  setOneTimeRebalanceOpen(true);
                } else {
                  props.onRiskChange(props.accountId, props.type, value);
                }
              }
            }}
            disabled={readOnly || profileLockActive}
            classes={sliderClasses}
            className={`${riskChange ? classes.riskChangeSlider : ''}`}
          />

          <ConfirmDialog
            disableClose
            title={`accountDetail_plan_oneTimeRebalancing_title_${automaticRebalancing === false ? 'off' : 'on'}`}
            btnCloseText="accountDetail_plan_oneTimeRebalancing_dont"
            btnConfirmText="accountDetail_plan_oneTimeRebalancing_do"
            isOpen={oneTimeRebalanceOpen}
            close={async () => {
              store && (await store.setOneTimeRebalancing(props.type, false));
              setOneTimeRebalanceOpen(false);
              props.onRiskChange(props.accountId, props.type, risk);
            }}
            confirm={async () => {
              store && (await store.setOneTimeRebalancing(props.type, true));
              setOneTimeRebalanceOpen(false);
              props.onRiskChange(props.accountId, props.type, risk);
            }}
          >
            <p>
              {t(`accountDetail_plan_oneTimeRebalancing_auto${automaticRebalancing === false ? 'Off' : 'On'}_info`)}
            </p>
            <p>
              {t(`accountDetail_plan_oneTimeRebalancing_auto${automaticRebalancing === false ? 'Off' : 'On'}_info2`)}
            </p>
          </ConfirmDialog>
        </Box>
        <div className={`${classes.bodyRow} ${classes.strategy}`}>
          <span>{`${t('bucket_investmentStrategy')}:`}</span>
          <span className={riskChange ? classes.riskChange : undefined}>
            {portfolio.code.type === PortfolioType.IND
              ? portfolio.code.title
              : translatePortfolioCode(portfolio.code.code)}
          </span>
        </div>

        <div className={`${classes.bodyRow}`}>
          <span>{portfolio.code.type !== PortfolioType.IND && `${t('bucket_portfolioType')}:`}</span>
          <span
            className={
              originalBucket && originalBucket.portfolio.code.type !== portfolio.code.type
                ? classes.riskChange
                : undefined
            }
          >
            {translatePortfolioType(portfolio.code.code, portfolio.code.type === PortfolioType.IND)}
          </span>
        </div>

        <div className={classes.bodyRow}>
          <span>{`${t('bucket_calculatedOutcome')}:`}</span>
          <Info
            content={t('tooltip_plan_calculatedIncome')}
            iconProps={{ fontSize: 'small' }}
            className={classes.tooltip}
          />
          <span className={riskChange ? classes.riskChange : undefined}>{fractionToPercent(portfolio.income)}</span>
        </div>
        <div className={classes.bodyRow}>
          <span>{`${t('bucket_acceptedDown')}:`}</span>
          <Info
            content={t('tooltip_plan_acceptedLoss')}
            iconProps={{ fontSize: 'small' }}
            className={classes.tooltip}
          />
          <span className={riskChange ? classes.riskChange : undefined}>{fractionToPercent(portfolio.loss)}</span>
        </div>
        {showAdditionalInfo ? (
          <>
            <div className={classes.bodyRow}>
              <span>{t('accountDetail_plan_automaticRebalancing')}</span>
              {(!userMode || automaticRebalancing) && (
                <Info
                  content={
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t(
                          `tooltip_plan_rebalancing${!rebalancingEnabled && !automaticRebalancing ? '_disabled' : ''}`,
                          {
                            interpolation: { escapeValue: false },
                          },
                        ),
                      }}
                    />
                  }
                  iconProps={{ fontSize: 'small' }}
                  className={classes.tooltip}
                />
              )}
              <span>{automaticRebalancing ? t('general_yes') : t('general_no')} </span>
            </div>
            {currency === Currency.CZK && (
              <div className={classes.bodyRow}>
                <span>{t('accountDetail_plan_currencyHedging')}</span>
                {!userMode && (
                  <Info
                    content={
                      !detailMode ? (
                        t('accountDetail_plan_currencyHedging_notSet_tooltip')
                      ) : (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: t('accountDetail_plan_currencyHedging_tooltip', {
                              interpolation: { escapeValue: false },
                            }),
                          }}
                        />
                      )
                    }
                    iconProps={{ fontSize: 'small' }}
                    className={classes.tooltip}
                  />
                )}
                <span>
                  {!detailMode
                    ? t('accountDetail_plan_currencyHedging_notSet')
                    : currencyHedging
                    ? t('general_yes')
                    : t('general_no')}
                </span>
              </div>
            )}
          </>
        ) : (
          <>
            <div className={classes.bodyRow}>
              <FreeSwitch
                id={`buckets[${thisBucketNumber}].automaticRebalancing`}
                label={`${t('accountDetail_plan_automaticRebalancing')}:`}
                checked={automaticRebalancing}
                tooltipContent={
                  !userMode || automaticRebalancing ? (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t(
                          `tooltip_plan_rebalancing${!rebalancingEnabled && !automaticRebalancing ? '_disabled' : ''}`,
                          {
                            interpolation: { escapeValue: false },
                          },
                        ),
                      }}
                    />
                  ) : undefined
                }
                afterChange={
                  store
                    ? () => {
                        store.switchBucketSetting('rebalancing', props.type);
                      }
                    : undefined
                }
                disabled={readOnly || !rebalancingEnabled}
                transparent
                fullWidth
                tooltipInside
                small
                warning={!!originalBucket && originalBucket.automaticRebalancing !== automaticRebalancing}
                dropdownFn={() => setTimeTestOpen(!timeTestOpen)}
              />
            </div>

            {timeTestOpen && (
              <div className={classes.bodyRow}>
                <FreeSwitch
                  id={`buckets[${thisBucketNumber}].rebalancingTimeTest`}
                  label={`${t('accountDetail_plan_rebalancingTimeTest')}:`}
                  checked={rebalancingTimeTest}
                  tooltipContent={
                    !userMode ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: t('tooltip_plan_rebalancingTimeTest', {
                            interpolation: { escapeValue: false },
                          }),
                        }}
                      />
                    ) : undefined
                  }
                  afterChange={
                    store
                      ? () => {
                          if (rebalancingTimeTest && store.data?.fixedTax) setFixedTaxWarning(true);
                          store.switchBucketSetting('time', props.type);
                        }
                      : undefined
                  }
                  disabled={readOnly || !automaticRebalancing || !rebalancingEnabled || rebalancingTimeTestDisabled}
                  transparent
                  fullWidth
                  tooltipInside
                  small
                />

                <ConfirmDialog
                  isOpen={fixedTaxWarning}
                  noHeader
                  close={() => setFixedTaxWarning(false)}
                  btnCloseText="general_ok"
                  maxWidth="sm"
                >
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t('accountDetail_plan_rebalancingTimeTestOff_fixedTax', {
                        interpolation: { escapeValue: false },
                      }),
                    }}
                  />
                </ConfirmDialog>
              </div>
            )}

            {currency === Currency.CZK && (
              <div className={classes.bodyRow} style={{ position: 'relative' }}>
                <FreeSwitch
                  id={`buckets[${thisBucketNumber}].currencyHedging`}
                  label={`${t('accountDetail_plan_currencyHedging')}:`}
                  checked={currencyHedging}
                  tooltipContent={
                    !userMode ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: t(
                            detailMode
                              ? 'accountDetail_plan_currencyHedging_tooltip'
                              : 'accountDetail_plan_currencyHedging_notSet_tooltip',
                            {
                              interpolation: { escapeValue: false },
                            },
                          ),
                        }}
                      />
                    ) : undefined
                  }
                  afterChange={
                    // TODO: remove
                    store
                      ? () => {
                          store.switchBucketSetting('hedging', props.type);
                        }
                      : undefined
                  }
                  disabled={true} //readOnly || !currencyHedgingEnabled
                  transparent
                  fullWidth
                  tooltipInside
                  small
                  warning={!!originalBucket && originalBucket.currencyHedging !== currencyHedging}
                  disabledTooltip={
                    detailMode
                      ? t('accountDetail_plan_currencyHedgingShort_tooltip')
                      : t('accountDetail_plan_currencyHedging_notSet_tooltip')
                  }
                />
              </div>
            )}

            {!timeTestOpen && <div className={`${classes.bodyRow} ${classes.dummy}`} />}
          </>
        )}
        {showDetail && !overview && (
          <div className={`${classes.bodyRow} button`}>
            <Button
              color={props.activated ? 'secondary' : 'default'}
              variant={props.activated ? 'outlined' : 'contained'}
              onClick={(e) => {
                showDetail(props.activated ? undefined : type);
                e.stopPropagation();
              }}
              className={`corners ${classes.hoverFix}`}
            >
              {t(`general_detail_${props.activated ? 'hide' : 'show'}`)}
              {props.activated ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};

export default observer(BucketComponent);
